import {
  Bullhorn,
  Chain,
  Events,
  TrashCan,
  Comment,
  SupportingFile,
  Accepted,
  Rejected,
  Undo,
} from 'components/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ActivityHeader } from 'components/activity-header';
import { AuditSegments } from 'components/audit-segments';
import type {
  AuditSegment,
  ReconcileAuditDetails,
  ReconcileEntityHistory,
} from 'types/api-responses';
import { EVENT_KINDS, EventRendererProps } from './types';
import {
  VerificationCorrectContent,
  VerificationIncorrectContent,
  VerificationRevertedContent,
  VerificationEventPayload,
} from './verification-event-content';
import style from './event-renderer.module.scss';
import classNames from 'classnames';

const getIconForEventKind = (kind: string) => {
  switch (kind) {
    case EVENT_KINDS.CREATE_COMMENT:
      return Comment;
    case EVENT_KINDS.CREATE_SUPPORTING_FILE:
      return SupportingFile;
    case EVENT_KINDS.DESTROY_SUPPORTING_FILE:
      return TrashCan;
    case EVENT_KINDS.COMPLETION_CORRECT:
      return Accepted;
    case EVENT_KINDS.COMPLETION_INCORRECT:
      return Rejected;
    case EVENT_KINDS.COMPLETION_REVERTED:
      return Undo;
    case EVENT_KINDS.IC_SLOT_UNCONFIRMED:
      return Undo;
    case EVENT_KINDS.IC_AUTO_CONFIRM_RECOMPUTED:
      return Accepted;
    case EVENT_KINDS.RESOLVE_REPORT:
      return Accepted;
    case EVENT_KINDS.REOPEN_REPORT:
      return Undo;
    case EVENT_KINDS.JIRA_COMMENT:
      return Comment;
    // Chain rather than a people icon: there is no team glyph in the set, and this row is
    // about a ticket being associated with a team, which is what Chain already means here.
    case EVENT_KINDS.JIRA_TEAM:
      return Chain;
    case EVENT_KINDS.JIRA_STATUS:
      return Events;
    case EVENT_KINDS.JIRA_MOVED:
      return Chain;
    default:
      return Bullhorn;
  }
};

// resolve_report, written by BugsFeatures::ResolutionsController#record_event. Snake_case:
// Event payloads are stored as-written and this feed does not camelize them.
// jira_status and jira_moved, written by Jira::RefreshFields#record_change_events. For a
// status change from/to are status names; for a move they are issue keys.
interface JiraChangePayload {
  from?: string | null;
  to?: string | null;
  boardName?: string | null;
}

// jira_team, written by Jira::RefreshFields#record_team_event. Any of these can be absent:
// a first assignment has no `from`, a removal has no `to`.
interface JiraTeamPayload {
  from_id?: string | null;
  from_name?: string | null;
  to_id?: string | null;
  to_name?: string | null;
}

interface ResolveReportPayload {
  resolution?: string;
  root_cause?: string;
  resolution_label?: string;
  root_cause_label?: string;
  note?: string;
}

// jira_comment, written by Jira::RefreshFields when a new visible comment appears.
interface JiraCommentPayload {
  body_markdown?: string;
}

// "not_a_bug" -> "Not a bug". Only a fallback for rows recorded before the server started
// putting labels in the payload; the label is always preferred when present.
const humanizeCode = (value?: string) => {
  if (!value) return null;

  const words = value.replace(/_/g, ' ');

  return words.charAt(0).toUpperCase() + words.slice(1);
};

// Payload of interest_certification.auto_confirm_recomputed (camelCased by
// the IC activity-feed serializer; written by AutoConfirmer).
interface AutoConfirmRecomputedPayload {
  thresholdPct?: number;
  confirmedCount?: number;
  alreadyConfirmedCount?: number;
  skippedCount?: number;
}

// An entity ref segment for AuditSegments, coalescing a null snapshot name to an
// id-based fallback (the same shape the backend uses for soft-deleted entities).
const entitySegment = (id: number, name: string | null): AuditSegment => ({
  entityName: name ?? `Entity ${id}`,
  entityId: id,
});

// One per-entity history line as audit segments: the entity, what happened to it,
// and the survivor it landed on — aliased rows append their rationale when one
// was recorded (undo rows carry deduplication history only).
const historySegments = (entry: ReconcileEntityHistory): AuditSegment[] => {
  const entity = entitySegment(entry.entityId, entry.entityName);
  const survivor = entitySegment(entry.survivorId, entry.survivorName);

  if (entry.action === 'deduplicated') {
    return [entity, ' was deduplicated into ', survivor, '.'];
  }

  return [
    entity,
    ' was aliased to ',
    survivor,
    entry.rationale ? ` — ${entry.rationale}` : '',
    '.',
  ];
};

const EventContent = ({
  kind,
  payload,
  details,
}: {
  kind: string;
  payload: Record<string, unknown>;
  details?: ReconcileAuditDetails;
}) => {
  const verificationPayload = payload as VerificationEventPayload;

  // Resolution and reopen. Labels are read from the payload where the server put them and
  // fall back to the raw code humanized, so rows written before the labels were recorded
  // still say something rather than nothing.
  if (kind === EVENT_KINDS.RESOLVE_REPORT) {
    const p = payload as ResolveReportPayload;
    const parts = [
      p.resolution_label || humanizeCode(p.resolution),
      p.root_cause_label || humanizeCode(p.root_cause),
      p.note?.toString().trim() || null,
    ].filter(Boolean);

    return <p>{parts.join(' — ')}</p>;
  }

  if (kind === EVENT_KINDS.REOPEN_REPORT) {
    return <p>Reopened</p>;
  }

  // A Jira workflow status change. from is always present — the detector skips the first
  // sync, where a report goes from no status to its initial one, because that is the
  // ticket being created rather than anyone moving it.
  if (kind === EVENT_KINDS.JIRA_STATUS) {
    const p = payload as JiraChangePayload;

    return (
      <p>
        Status moved from {p.from} to {p.to}
      </p>
    );
  }

  // The ticket changed Jira project. The board name is what a reader recognises; the keys
  // are what they will search for, so both are shown.
  if (kind === EVENT_KINDS.JIRA_MOVED) {
    const p = payload as JiraChangePayload;
    const board = p.boardName?.toString();
    const keys = [p.from, p.to].filter(Boolean).join(' → ');

    return (
      <p>
        Moved to {board || 'another board'}
        {keys ? ` — ${keys}` : ''}
      </p>
    );
  }

  // Triage assigning a team in Jira. Three shapes rather than one sentence with blanks in
  // it: a first assignment, a move between teams, and a removal each read differently, and
  // "Team changed from  to Eval Lib" is worse than any of them.
  if (kind === EVENT_KINDS.JIRA_TEAM) {
    const p = payload as JiraTeamPayload;
    const from = p.from_name?.toString();
    const to = p.to_name?.toString();

    if (!to) return <p>Team removed{from ? ` (was ${from})` : ''}</p>;

    return (
      <p>{from ? `Team moved from ${from} to ${to}` : `Team set to ${to}`}</p>
    );
  }

  // A comment written in Jira. Rendered through react-markdown, never innerHTML: the body is
  // typed by anyone with access to the Jira ticket, and the fallback branch further down
  // hands payload.content straight to dangerouslySetInnerHTML.
  if (kind === EVENT_KINDS.JIRA_COMMENT) {
    const body = (payload as JiraCommentPayload).body_markdown;

    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {body?.toString() ?? ''}
      </ReactMarkdown>
    );
  }

  if (kind === EVENT_KINDS.IC_SLOT_UNCONFIRMED) {
    return <p>Unconfirmed interest — confirmation removed</p>;
  }

  if (kind === EVENT_KINDS.IC_AUTO_CONFIRM_RECOMPUTED) {
    const {
      thresholdPct,
      confirmedCount,
      alreadyConfirmedCount,
      skippedCount,
    } = payload as AutoConfirmRecomputedPayload;
    return (
      <div>
        <p>Auto-confirm recomputed at {thresholdPct ?? '--'}% tolerance</p>
        <p>
          {confirmedCount ?? 0} confirmed, {alreadyConfirmedCount ?? 0} already
          confirmed, {skippedCount ?? 0} skipped
        </p>
      </div>
    );
  }

  if (kind === EVENT_KINDS.COMPLETION_INCORRECT) {
    return <VerificationIncorrectContent payload={verificationPayload} />;
  }

  if (kind === EVENT_KINDS.COMPLETION_CORRECT) {
    return <VerificationCorrectContent payload={verificationPayload} />;
  }

  if (kind === EVENT_KINDS.COMPLETION_REVERTED) {
    return <VerificationRevertedContent payload={verificationPayload} />;
  }

  // Reconcile / undo rows ship their summary as plain text plus structured
  // detail bullets — laid out here, mirroring the server-rendered <details>
  // block this replaces.
  if (details) {
    const summary =
      typeof payload.content === 'string' ? payload.content : null;
    const aliasRationales = details.aliasRationales ?? [];
    const perEntityHistory = details.perEntityHistory ?? [];
    return (
      <details open>
        <summary>
          <strong>{details.label}</strong>
          {summary ? <> — {summary}</> : null}
        </summary>
        {details.bullets.map((line, i) => (
          <div key={i}>
            <AuditSegments segments={line} />
          </div>
        ))}
        {aliasRationales.length > 0 && (
          <details className={style.subEntry}>
            <summary>Alias rationales</summary>
            <ul className={style.subEntryList}>
              {aliasRationales.map((entry) => (
                <li key={entry.survivorId}>
                  <AuditSegments
                    segments={[
                      entitySegment(entry.survivorId, entry.survivorName),
                      `: ${entry.rationale}`,
                    ]}
                  />
                </li>
              ))}
            </ul>
          </details>
        )}
        {perEntityHistory.length > 0 && (
          <details className={style.subEntry}>
            <summary>Per-entity history</summary>
            <ul className={style.subEntryList}>
              {perEntityHistory.map((entry, i) => (
                <li key={`${entry.entityId}-${i}`}>
                  <AuditSegments segments={historySegments(entry)} />
                </li>
              ))}
            </ul>
          </details>
        )}
      </details>
    );
  }

  if (payload.content) {
    const content = Array.isArray(payload.content)
      ? payload.content.join('')
      : String(payload.content);
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }

  return null;
};

export const EventRenderer = ({
  kind,
  createdBy,
  createdAt,
  payload,
  details,
  contentClassName,
  contentPrefix,
}: EventRendererProps & {
  details?: ReconcileAuditDetails;
  contentClassName?: string;
  contentPrefix?: React.ReactNode;
}) => {
  const Icon = getIconForEventKind(kind);

  return (
    <>
      <ActivityHeader Icon={Icon} createdBy={createdBy} createdAt={createdAt} />
      <div className={classNames(style.content, contentClassName)}>
        {contentPrefix}
        <EventContent kind={kind} payload={payload} details={details} />
      </div>
    </>
  );
};
